import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, Save, Search, Shield, X, AlertCircle } from 'lucide-react'
import {
  useCreateAdRule, useUpdateAdRule, useSearchAdGroups, useAdUsers,
} from '../../hooks/useAdDomains'

const ROLES = [
  { value: 'Viewer',   label: 'Viewer'   },
  { value: 'Operator', label: 'Operator' },
  { value: 'Admin',    label: 'Admin'    },
]

export default function CreateRuleModal({ configId, contextGroup, contextUser, editRule, onClose, onCreated }) {
  const { t } = useTranslation()
  const isEdit = !!editRule
  const { results: groupResults, isSearching, search } = useSearchAdGroups(configId)
  const createRule = useCreateAdRule(configId)
  const updateRule = useUpdateAdRule(configId)
  const { data: allAdUsers } = useAdUsers()
  const timerRef = useRef(null)

  const contextGroupDn = editRule?.adGroupDn ?? contextGroup?.dn ?? contextGroup?.distinguishedName ?? ''
  const contextGroupName = editRule?.adGroupDisplayName ?? contextGroup?.displayName ?? ''

  const resolvedUserId = editRule
    ? editRule.userId
    : (contextUser
      ? (contextUser.userId
         ?? (allAdUsers ?? []).find(
              (u) => u.tenantAdConfigId === configId &&
                     (u.distinguishedName?.toLowerCase() === contextUser.dn?.toLowerCase()
                      || u.samAccountName?.toLowerCase() === contextUser.samAccountName?.toLowerCase()))?.userId
         ?? null)
      : null)

  const [scope, setScope] = useState(
    isEdit
      ? (editRule.userId ? 'user' : 'group')
      : (contextUser ? (resolvedUserId ? 'user' : 'group') : 'group')
  )
  const [form, setForm] = useState({
    adGroupDn: contextGroupDn,
    adGroupDisplayName: contextGroupName,
    mappedRole: editRule?.mappedRole ?? 'Operator',
    allowAccess: editRule?.allowAccess ?? true,
    priority: editRule?.priority ?? (contextUser ? 10 : 100),
  })
  const [groupQuery, setGroupQuery] = useState(contextGroupName)
  const [showDropdown, setShowDropdown] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setScope(contextUser ? (resolvedUserId ? 'user' : 'group') : 'group')
  }, [contextUser, resolvedUserId])

  const handleGroupSearch = (e) => {
    const v = e.target.value
    setGroupQuery(v)
    setForm((f) => ({ ...f, adGroupDn: '', adGroupDisplayName: '' }))
    setShowDropdown(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => search(v), 350)
  }

  const selectGroup = (g) => {
    setForm((f) => ({ ...f, adGroupDn: g.dn, adGroupDisplayName: g.displayName }))
    setGroupQuery(g.displayName)
    setShowDropdown(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.adGroupDn) { setError(t('domains.manageModal.ruleSelectGroup', 'Selecione um grupo')); return }
    if (!isEdit && scope === 'user' && !resolvedUserId) {
      setError(t('domains.dashboard.userNeverLogged', 'Este usuário ainda não logou no sistema; crie a regra em nível de grupo primeiro.'))
      return
    }
    setError('')
    setSubmitting(true)
    try {
      if (isEdit) {
        await updateRule.mutateAsync({
          ruleId: editRule.id,
          data: {
            adGroupDisplayName: form.adGroupDisplayName,
            mappedRole: form.mappedRole,
            allowAccess: form.allowAccess,
            priority: Number(form.priority),
          },
        })
        onCreated?.()
        onClose?.()
        return
      }

      await createRule.mutateAsync({
        ...form,
        userId: scope === 'user' ? resolvedUserId : null,
      })

      onCreated?.()
      onClose?.()
    } catch (ex) {
      setError(ex?.response?.data?.error || ex?.message || String(ex))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="rounded-xl p-2 bg-theme-primary/10 text-theme-primary">
              <Shield className="w-4 h-4" />
            </div>
            <h3 className="font-semibold text-gray-900">
              {isEdit
                ? t('domains.dashboard.editRuleTitle', 'Editar regra')
                : t('domains.dashboard.createRuleTitle', 'Nova regra de acesso')}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {contextUser && !isEdit && (
            <div className="flex rounded-lg bg-gray-100 p-1 text-xs">
              <button
                type="button"
                onClick={() => setScope('group')}
                className={`flex-1 py-1.5 rounded ${scope === 'group' ? 'bg-white shadow font-medium' : 'text-gray-500'}`}
              >
                {t('domains.dashboard.scopeGroup', 'Para o grupo')}
              </button>
              <button
                type="button"
                onClick={() => setScope('user')}
                disabled={!resolvedUserId}
                title={!resolvedUserId ? t('domains.dashboard.userNeverLogged', 'Usuário ainda não logou no sistema') : ''}
                className={`flex-1 py-1.5 rounded ${scope === 'user' ? 'bg-white shadow font-medium' : 'text-gray-500'} disabled:opacity-40`}
              >
                {t('domains.dashboard.scopeUser', 'Para este usuário')}
              </button>
            </div>
          )}

          {scope === 'user' && contextUser && (
            <div className="text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded px-2 py-1.5">
              {t('domains.dashboard.ruleOverrideFor', 'Override individual para')}: <b>{contextUser.displayName || contextUser.samAccountName}</b>
            </div>
          )}

          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('domains.manageModal.ruleGroup', 'Grupo AD')}
            </label>
            {contextGroupDn ? (
              <div className="text-xs bg-theme-primary/10 text-theme-primary px-2 py-1.5 rounded font-mono truncate">
                {contextGroupName} <span className="opacity-60">— {contextGroupDn}</span>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  className="input w-full pl-8 text-sm"
                  placeholder={t('domains.manageModal.ruleGroupPlaceholder', 'Buscar grupo no AD...')}
                  value={groupQuery}
                  onChange={handleGroupSearch}
                  onFocus={() => setShowDropdown(true)}
                  autoComplete="off"
                />
                {isSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
                {showDropdown && groupResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
                    {groupResults.map((g) => (
                      <button
                        key={g.dn}
                        type="button"
                        onClick={() => selectGroup(g)}
                        className="w-full text-left px-3 py-2 hover:bg-theme-primary/10"
                      >
                        <p className="text-sm font-medium text-gray-900">{g.displayName}</p>
                        <p className="text-xs text-gray-400 font-mono truncate">{g.dn}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('domains.manageModal.ruleRole', 'Role')}
              </label>
              <select
                className="input w-full text-sm"
                value={form.mappedRole}
                onChange={(e) => setForm((f) => ({ ...f, mappedRole: e.target.value }))}
              >
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('domains.manageModal.ruleAccess', 'Acesso')}
              </label>
              <select
                className="input w-full text-sm"
                value={form.allowAccess ? 'allow' : 'deny'}
                onChange={(e) => setForm((f) => ({ ...f, allowAccess: e.target.value === 'allow' }))}
              >
                <option value="allow">{t('domains.manageModal.ruleAllow', 'Permite')}</option>
                <option value="deny">{t('domains.manageModal.ruleDeny', 'Nega')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('domains.manageModal.rulePriority', 'Prioridade')}
              </label>
              <input
                type="number"
                className="input w-full text-sm"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
                min={1}
              />
            </div>
          </div>

          <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
            {t('domains.dashboard.firewallHint', 'Acesso VPN a IPs específicos é configurado agora no firewall de cada ManagedDevice (aba Input → Grupo AD como origem).')}
          </div>
        </form>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose} className="btn btn-secondary text-xs">
            {t('common.buttons.cancel', 'Cancelar')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !form.adGroupDn}
            className="btn btn-primary text-xs flex items-center gap-1"
          >
            {submitting
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : (isEdit ? <Save className="w-3 h-3" /> : <Plus className="w-3 h-3" />)}
            {isEdit
              ? t('common.buttons.save', 'Salvar alterações')
              : t('domains.manageModal.newRule', 'Criar regra')}
          </button>
        </div>
      </div>
    </div>
  )
}
